// src/components/BulkUploadVulnerabilityModal.js
import React, { useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { X, Upload, AlertTriangle, FileText, CheckCircle, Info } from 'lucide-react';

const BulkUploadVulnerabilityModal = ({ isOpen, onClose, onUploadComplete }) => {
  const { getToken } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setUploadResult(null);

      // Preview first few lines
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split('\n').slice(0, 6);
        setPreview(lines.join('\n'));
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/vulnerabilities/bulk-upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      
      setUploadResult(data.results);

      if (data.results.success > 0 && onUploadComplete) {
        // Wait a moment to show results, then refresh
        setTimeout(() => {
          onUploadComplete();
        }, 2000);
      }

    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError(err.message || 'Failed to upload CSV file');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700/50 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between bg-gradient-to-r from-cyan-500/10 to-blue-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-100">Bulk Upload CVEs</h2>
              <p className="text-sm text-slate-400">Upload a CSV file with multiple vulnerabilities</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-200 transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {uploadResult && (
            <div className={`mb-4 p-4 rounded-lg border flex items-start gap-3 ${
              uploadResult.failed === 0
                ? 'bg-green-500/10 border-green-500/20'
                : 'bg-yellow-500/10 border-yellow-500/20'
            }`}>
              <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${
                uploadResult.failed === 0 ? 'text-green-400' : 'text-yellow-400'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  uploadResult.failed === 0 ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  Upload Complete
                </p>
                <div className="mt-2 text-sm text-slate-300 space-y-1">
                  <p>✅ <strong>{uploadResult.success}</strong> vulnerabilities uploaded successfully</p>
                  {uploadResult.duplicates > 0 && (
                    <p>⚠️  <strong>{uploadResult.duplicates}</strong> duplicates skipped</p>
                  )}
                  {uploadResult.failed > 0 && (
                    <p>❌ <strong>{uploadResult.failed}</strong> failed to upload</p>
                  )}
                  <p className="text-slate-400">Total processed: {uploadResult.total}</p>
                </div>
                
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-red-400 mb-1">Errors:</p>
                    <ul className="text-xs text-red-300 space-y-1 max-h-32 overflow-y-auto">
                      {uploadResult.errors.map((err, idx) => (
                        <li key={idx} className="font-mono">
                          {err.cve_id || `Row ${err.row}`}: {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CSV Format Guide */}
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-cyan-400 mt-0.5" />
                <h3 className="text-sm font-medium text-slate-300">CSV Format Requirements</h3>
              </div>
              <button
                onClick={() => {
                  // Create CSV template
                  const template = `cve_id,title,severity,cvss_score,published_date,last_modified,affected_products,vendor,description,exploit_available,patch_available,workaround,reference_urls,cwe,in_the_wild,cpes
CVE-2025-12345,Example SQL Injection Vulnerability,High,9.8,2025-01-15,2025-01-20,{MySQL Server;PostgreSQL},Database Vendor,This is a sample vulnerability description showing SQL injection in database software,t,t,Apply security patches immediately,{https://nvd.nist.gov/vuln/detail/CVE-2025-12345;https://vendor.com/security},CWE-89,t,{cpe:2.3:a:vendor:product:1.0:*:*:*:*:*:*:*}
CVE-2025-67890,Example XSS Vulnerability,Medium,6.1,2025-02-01,,Web Application;Mobile App,Web Company,Cross-site scripting vulnerability in web application,t,,,{https://example.com/advisory},CWE-79,,
CVE-2025-11111,Example Authentication Bypass,Critical,10.0,2025-01-10,2025-01-12,{Authentication Service},Auth Provider,Authentication bypass allows unauthorized access,t,t,Upgrade to version 2.0,{https://auth-provider.com/fix},CWE-287,t,`;

                  const blob = new Blob([template], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'vulnerability_upload_template.csv';
                  a.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="px-3 py-1 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded transition-colors flex items-center gap-1"
              >
                <FileText className="w-3 h-3" />
                Download Template
              </button>
            </div>
            
            <div className="space-y-2 text-xs">
              <div>
                <p className="text-slate-400">
                  <span className="text-cyan-400 font-medium">Required columns:</span> cve_id, title
                </p>
              </div>
              
              <div>
                <p className="text-slate-400">
                  <span className="text-blue-400 font-medium">Optional columns:</span> severity, cvss_score, description, vendor, 
                  affected_products, exploit_available, patch_available, published_date, 
                  last_modified, reference_urls, cwe, workaround, in_the_wild, cpes
                </p>
              </div>

              <div className="pt-2 border-t border-slate-700/50">
                <p className="text-slate-400 mb-1">
                  <span className="text-green-400 font-medium">Boolean fields</span> (exploit_available, patch_available, in_the_wild):
                </p>
                <ul className="ml-4 space-y-1 text-slate-500">
                  <li>• TRUE values: <code className="text-green-400">t, true, 1, yes</code> (case insensitive)</li>
                  <li>• FALSE values: <code className="text-slate-400">empty field, f, false, 0, no</code></li>
                  <li>• <span className="text-yellow-400">Empty fields default to FALSE</span></li>
                </ul>
              </div>

              <div className="pt-2 border-t border-slate-700/50">
                <p className="text-slate-400 mb-1">
                  <span className="text-purple-400 font-medium">Array fields</span> (affected_products, reference_urls, cpes):
                </p>
                <ul className="ml-4 space-y-1 text-slate-500">
                  <li>• PostgreSQL format: <code className="text-cyan-400">{`{item1,item2,item3}`}</code></li>
                  <li>• Semicolon format: <code className="text-cyan-400">item1;item2;item3</code></li>
                  <li>• Empty fields are allowed (will be stored as empty array)</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-slate-700/50">
                <p className="text-slate-400 mb-1">
                  <span className="text-orange-400 font-medium">Empty field handling:</span>
                </p>
                <ul className="ml-4 space-y-1 text-slate-500">
                  <li>• Empty text fields → stored as NULL in database</li>
                  <li>• Empty boolean fields → stored as FALSE</li>
                  <li>• Empty array fields → stored as empty array []</li>
                  <li>• Empty numeric fields (cvss_score) → stored as NULL</li>
                </ul>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div
            onClick={() => !loading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              loading 
                ? 'border-slate-700 cursor-not-allowed opacity-50' 
                : 'border-slate-700 cursor-pointer hover:border-cyan-500/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              disabled={loading}
            />
            {file ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-8 h-8 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-200">{file.name}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {(file.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                {!loading && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                    }}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Remove file
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Upload className="w-8 h-8 text-slate-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-300">
                    Click to select CSV file
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    or drag and drop
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          {preview && (
            <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <h3 className="text-sm font-medium text-slate-300 mb-2">Preview (first 6 lines)</h3>
              <pre className="text-xs text-slate-400 overflow-x-auto font-mono">
                {preview}
              </pre>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between bg-slate-800/30">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-slate-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload CSV
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadVulnerabilityModal;